import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const slides = [
  {
    img: 'https://public.readdy.ai/ai/img_res/5e82f444769fb274e1f9e9fb8c991930.jpg',
    label: 'Frameless Shower Doors',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=modern%20frameless%20glass%20stair%20railing%20in%20luxury%20home%20interior%2C%20clear%20tempered%20glass%20panels%20with%20stainless%20steel%20posts%2C%20open%20concept%20living%20space%2C%20natural%20light%20flooding%20in%2C%20professional%20interior%20design%20photography&width=1920&height=1080&seq=hero002&orientation=landscape',
    label: 'Glass Railings',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=large%20custom%20frameless%20mirror%20wall%20in%20modern%20luxury%20bathroom%20with%20double%20vanity%2C%20LED%20lighting%2C%20white%20marble%20countertop%2C%20elegant%20interior%20design%2C%20professional%20photography&width=1920&height=1080&seq=hero003&orientation=landscape',
    label: 'Custom Mirrors',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=luxury%20glass%20bathtub%20enclosure%20with%20clear%20tempered%20glass%20panels%2C%20freestanding%20tub%2C%20marble%20floor%2C%20warm%20lighting%2C%20modern%20bathroom%20interior%2C%20professional%20architectural%20photography&width=1920&height=1080&seq=hero004&orientation=landscape',
    label: 'Tub Enclosures',
  },
];

const HeroSection = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const textRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Slideshow
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrent(prev => (prev + 1) % slides.length);
        setAnimating(false);
      }, 600);
    }, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // Parallax + scroll progress
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Text entrance
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setTimeout(() => el.classList.add('opacity-100', 'translate-y-0'), 200);
  }, []);

  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 z-[100] h-0.5 bg-white/30 w-full pointer-events-none">
        <div
          className="h-full bg-white transition-all duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <section className="relative w-full h-screen min-h-[700px] overflow-hidden">
        {/* Slides */}
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === current ? (animating ? 'opacity-0' : 'opacity-100') : 'opacity-0'
            }`}
          >
            <img
              src={slide.img}
              alt={slide.label}
              className="w-full h-full object-cover object-top"
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'low'}
              decoding={i === 0 ? 'sync' : 'async'}
              style={{
                transform: `scale(1.08) translateY(${scrollY * 0.15}px)`,
                transition: 'transform 0.1s linear',
              }}
            />
          </div>
        ))}

        {/* Glass reflection overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/65 via-black/40 to-black/55 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)',
          }}
        />

        {/* Slide indicators */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`transition-all duration-500 rounded-full cursor-pointer ${
                i === current ? 'w-8 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>

        {/* Current slide label */}
        <div className="absolute top-28 right-6 md:right-10 z-20 hidden sm:block">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4">
            <div className="flex items-center gap-2 mb-2">
              {[1,2,3,4,5].map(i => (
                <i key={i} className="ri-star-fill text-yellow-400 text-xs"></i>
              ))}
            </div>
            <p className="text-white font-bold text-sm">500+ 5-Star Reviews</p>
            <p className="text-white/60 text-xs mt-0.5">Google &amp; Yelp Verified</p>
            <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/15">
              <i className="ri-shield-check-line text-white/70 text-xs"></i>
              <p className="text-white/60 text-xs">Insured &amp; Bonded</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          ref={textRef}
          className="relative z-10 h-full flex flex-col justify-center px-6 md:px-16 lg:px-24 opacity-0 translate-y-8 transition-all duration-1000 ease-out"
        >
          <div className="max-w-3xl">
            <div className="overflow-hidden mb-6">
              <span className="inline-block text-white/70 text-xs font-semibold uppercase tracking-widest border border-white/25 px-4 py-1.5 rounded-full animate-fade-in-up">
                {t('hero_badge')}
              </span>
            </div>

            <h1
              className="text-4xl md:text-6xl lg:text-7xl font-light text-white leading-tight mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <span className="block overflow-hidden">
                <span className="block animate-slide-up-1">{t('hero_headline1')}</span>
              </span>
              <span className="block overflow-hidden">
                <span className="block font-semibold animate-slide-up-2">{t('hero_headline2')}</span>
              </span>
              <span className="block overflow-hidden">
                <span className="block animate-slide-up-3">{t('hero_headline3')}</span>
              </span>
            </h1>

            <p className="text-white/80 text-lg md:text-xl font-light leading-relaxed mb-10 max-w-xl animate-fade-in-delay">
              {t('hero_sub')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-delay2">
              <Link
                to="/services"
                className="group relative overflow-hidden bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
              >
                <span className="relative z-10">{t('getFreeQuote')}</span>
              </Link>
              <a
                href="tel:+17026042093"
                className="flex items-center justify-center gap-3 border border-white/50 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/10 hover:border-white transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 animate-pulse-ring">
                  <i className="ri-phone-fill text-xs"></i>
                </span>
                {t('callNow')}: (702) 604-2093
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mt-14 animate-fade-in-delay3">
              {[
                { num: t('hero_stat1_num'), label: t('hero_stat1_label') },
                { num: t('hero_stat2_num'), label: t('hero_stat2_label') },
                { num: t('hero_stat3_num'), label: t('hero_stat3_label') },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <p className="text-white text-2xl font-bold">{stat.num}</p>
                  <p className="text-white/50 text-xs uppercase tracking-wider mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
          <div className="w-5 h-8 border border-white/30 rounded-full flex items-start justify-center pt-1.5">
            <div className="w-1 h-2 bg-white/70 rounded-full animate-scroll-dot" />
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
